/**
 * User-Agent patterns for automated traffic — search/SEO crawlers, link-preview
 * bots, headless renderers, monitoring agents.
 *
 * Single source of truth shared by the two layers that must treat bots
 * identically for React hydration errors:
 *   - pages/_app.tsx `captureHydrationDiff` → skips logging `hydration_diff`
 *     for these UAs (our custom recorder).
 *   - src/utils/loggerClient.ts `beforeSend` → drops React #418/#423/#425
 *     (which Datadog's `forwardErrorsToLogs` catches independently of the
 *     recorder) for these UAs.
 *
 * Why bots only: strict bot renderers (Bingbot, HeadlessChrome, Lighthouse)
 * trip React 19's attribute/whitespace serialization compare on listing pages;
 * real users don't at meaningful rates. Keeping both consumers on this one
 * constant prevents the two filters from drifting apart.
 */
export const AUTOMATED_TRAFFIC_UA_REGEX =
  /Lighthouse|Googlebot|bingbot|Bingbot|AdsBot|PageSpeed|Spider|Crawl|HeadlessChrome|PetalBot|YandexBot|DuckDuckBot|SemrushBot|AhrefsBot|MJ12bot|DotBot|facebookexternalhit|Twitterbot|LinkedInBot|Slackbot|WhatsApp|TelegramBot|Discordbot|Bytespider/i

export const isAutomatedTrafficUA = (ua: string | undefined | null): boolean =>
  typeof ua === 'string' && AUTOMATED_TRAFFIC_UA_REGEX.test(ua)

/**
 * Link-preview unfurlers only — the strict subset of the automated-traffic
 * population that fetches a URL to build a chat/social share card: WhatsApp,
 * iMessage (which impersonates `facebookexternalhit` + `Twitterbot`), Slack,
 * Telegram, Facebook/Instagram, X, LinkedIn, Discord, Skype.
 *
 * Consumed by the SSR auth gates (`withAuthSSR`, the checkout wizard,
 * `/checkoutDetails/*`) to serve a meta-only 200 shell instead of the auth
 * 307: the redirect lands previewers on `/authenticate` | `/login`, where the
 * firewall's "Challenge crawler UAs on auth paths" rule (2026-08-27) returns
 * a challenge page with zero og tags — so every shared gated link unfurled as
 * a naked URL. Deliberately EXCLUDES search crawlers (Googlebot, bingbot…):
 * they have no business on gated pages and should keep getting the redirect.
 *
 * In-app browsers are safe from false positives: WhatsApp/FB open links in a
 * system browser or a webview whose UA says `FBAN`/`FB_IAB`/`wv` — never the
 * bot tokens below.
 */
export const LINK_PREVIEW_UA_REGEX =
  /\bWhatsApp\/|facebookexternalhit|Facebot|Twitterbot|LinkedInBot|Slackbot|TelegramBot|Discordbot|SkypeUriPreview/i

export const isLinkPreviewUA = (ua: string | undefined | null): boolean =>
  typeof ua === 'string' && LINK_PREVIEW_UA_REGEX.test(ua)
